/**
 * THE LEGACY RULE FOR A 401 WITHOUT A REASON, shared by App and Book.
 *
 * Moved unchanged from apps/app/src/lib/firebase.ts so Book applies the same
 * rule. Each app passes its own Firebase `currentUser`; nothing here imports
 * Firebase.
 *
 * A refresh failure is not itself proof that the identity is gone: a browser
 * can be offline or Firebase can be transiently unavailable. Only an absent
 * user or an SDK error from the explicit invalid-identity set authorises
 * signing out. Every other failure preserves the session and reaches the
 * caller's bounded retry UI.
 */

/** The only outcomes that let the Apollo boundary end a Firebase session. */
export type ForcedIdTokenRecovery =
  | { kind: "refreshed"; token: string }
  | { kind: "reauthenticate" }
  | { kind: "unavailable" };

export const INVALID_AUTH_REFRESH_CODES: ReadonlySet<string> = new Set([
  "auth/user-disabled",
  "auth/user-not-found",
  "auth/user-token-expired",
  "auth/invalid-user-token",
  "auth/user-mismatch",
]);

interface RefreshableUser {
  readonly uid: string;
  readonly isAnonymous: boolean;
  getIdToken(forceRefresh?: boolean): Promise<string>;
}

/**
 * ONE FORCED REFRESH IN FLIGHT PER UID (H2). When several credentialed
 * requests are refused with a no-reason 401 at once (a page firing parallel
 * reads as the token expires), each used to start its own getIdToken(true).
 * They now await the same refresh. Keyed by UID, so a different person never
 * shares one; the entry is removed when it settles, success or failure, so
 * the next refusal after that refreshes again. Page memory only.
 */
const inFlight = new Map<string, Promise<ForcedIdTokenRecovery>>();

/**
 * Refresh THE USER THE REFUSED REQUEST WAS SENT AS, and nobody else
 * (security review R1). Between the send and its 401 the person can change:
 * a sign-out, then a new anonymous guest. Refreshing whoever is current then
 * would replay the old query as them. So:
 *
 *  - no current user: `unavailable`. They are already signed out; there is
 *    nothing to end and nothing to replay.
 *  - an anonymous user, or a different UID: `unavailable`. No refresh, no
 *    replay, no signal.
 *
 * Those checks run per caller, BEFORE joining an in-flight refresh, so a
 * caller can only ever share a refresh of the UID its own request was sent
 * as. Joining does not change the session link's bound of one refresh per
 * request, and nothing here replays anything.
 */
export function forceRefreshForRecovery(
  user: RefreshableUser | null,
  expectedUid: string
): Promise<ForcedIdTokenRecovery> {
  if (!user || user.isAnonymous || user.uid !== expectedUid) return Promise.resolve({ kind: "unavailable" });

  const pending = inFlight.get(expectedUid);
  if (pending) return pending;

  const refresh: Promise<ForcedIdTokenRecovery> = refreshOnce(user).finally(() => {
    if (inFlight.get(expectedUid) === refresh) inFlight.delete(expectedUid);
  });
  inFlight.set(expectedUid, refresh);
  return refresh;
}

/** One getIdToken(true), classified. Never rejects. */
async function refreshOnce(user: RefreshableUser): Promise<ForcedIdTokenRecovery> {
  try {
    const token = await user.getIdToken(true);
    return token ? { kind: "refreshed", token } : { kind: "unavailable" };
  } catch (error) {
    const code =
      error && typeof error === "object" && typeof (error as { code?: unknown }).code === "string"
        ? (error as { code: string }).code
        : null;
    if (code && INVALID_AUTH_REFRESH_CODES.has(code)) return { kind: "reauthenticate" };
    console.error("Failed to force-refresh a Firebase ID token.", error);
    return { kind: "unavailable" };
  }
}
